import {
  DropResult,
  DragDropContext,
  Droppable,
  Draggable,
} from "@hello-pangea/dnd";
import { BodyTyp } from "../../../../../../../../components/Typography/Typography";
import { PathGradeConfigItem } from "../../../../../../../../types/api/configurations.type";
import {
  FontWeightEnum,
  IconSizeEnum,
  WithOptional,
} from "../../../../../../../../types/AppTypes";
import { theme } from "../../../../../../../../constants/StyledComponents";
import {
  StyledDroppableItem,
  StyledManagePathsGrades,
  StyledPathGradeItem,
  StyledEmptyItem,
  CreateElement,
} from "./PathsGradesDnd.styled";
import { BadgeIndex } from "../../../../../../../../components/BadgeIndex/BadgeIndex";
import { IconManager } from "../../../../../../../../components/core/IconManager/IconManager";
import {
  ReactComponent as PlusIcon,
  ReactComponent as AddIcon,
} from "../../../../../../../../icons/add.svg";
import React, { useEffect, useState } from "react";
import {
  CREATE_ELEMENT_LABEL,
  DELETE_ITEM_LABEL,
  DELETE_ITEM_MODAL_LABEL,
} from "../../../../../../../../constants/Dictionary";
import TextInput from "../../../../../../../../components/core/TextInput/TextInput";
import { ReactComponent as CheckIcon } from "../../../../../../../../icons/check.svg";
import { ReactComponent as EditIcon } from "../../../../../../../../icons/pencil.svg";
import { ReactComponent as BinIcon } from "../../../../../../../../icons/bin.svg";
import { ReactComponent as CloseIcon } from "../../../../../../../../icons/close.svg";
import {
  CheckIconContainer,
  StyledCloseIconContainer,
} from "../../../../../../../../components/GradePathItem/PathGradeItem.styled";
import { ModalDeleteItem } from "../ModalDeleteItem";
import { NewPathGradeConfigItem } from "../PathsDnd/PathsDnd";
import { GenericPathGradeItem } from "../ManagePathsGrades";
import { InputSizes } from "../../../../../../../../components/core/Input/Input";

export type JobFamily = {
  order: number;
  name: string;
  initials: string;
};

export type PathsGradesDndProps = {
  isEditable: boolean;
  gradesCount: number;
  pathsCount: number;
  pathGradeItems: NewPathGradeConfigItem[][];
  setPathGradeItems: (x: NewPathGradeConfigItem[][]) => void;
  setIsUpdatingAnItem: (x: boolean) => void;
  isUpdatingAnItem: boolean;
  gradeItems: GenericPathGradeItem[];
  pathItems: GenericPathGradeItem[];
  editingMainItem: boolean;
  activeItem: { row: number; column: number } | null;
  setActiveItem: (x: { row: number; column: number } | null) => void;
  editingItem: {
    pathPosition: number;
    gradePosition: number;
    value: string;
  } | null;
  setEditingItem: (
    x: {
      pathPosition: number;
      gradePosition: number;
      value: string;
    } | null,
  ) => void;
};

export const PathsGradesDnd = ({
  isEditable,
  gradesCount,
  pathsCount,
  pathGradeItems,
  setPathGradeItems,
  setIsUpdatingAnItem,
  isUpdatingAnItem,
  gradeItems,
  pathItems,
  editingMainItem,
  activeItem,
  setActiveItem,
  editingItem,
  setEditingItem,
}: PathsGradesDndProps) => {
  const [newItem, setNewItem] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);

  const handleOnDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    const sourcePosition = getNumbersAfterPrefix(source.droppableId, "source");
    const destPosition = getNumbersAfterPrefix(
      destination.droppableId,
      "destination",
    );

    if (sourcePosition && destPosition) {
      const newState = [...pathGradeItems];
      const item = JSON.parse(
        JSON.stringify(newState[sourcePosition.column][sourcePosition.row]),
      );

      newState[sourcePosition.column][sourcePosition.row] = null;
      newState[destPosition.column][destPosition.row] = updateMatrix(
        item,
        destPosition,
      );

      setPathGradeItems(newState);
    }
  };

  const updateMatrix = (
    item: PathGradeConfigItem,
    destPosition: { row: number; column: number },
  ) => {
    const sortGradeItems = [...gradeItems].reverse();
    const rowGrade = sortGradeItems[destPosition.row].id;
    const colPath = pathItems[destPosition.column].id;

    item = { ...item, pathId: colPath, gradeId: rowGrade };

    return item;
  };

  useEffect(() => {
    setActiveItem(null);
    setNewItem("");
  }, [!isEditable]);

  function getNumbersAfterPrefix(
    str: string,
    prefix: string,
  ): { row: number; column: number } | null {
    const match = str.match(`${prefix}-(\\d+)-(\\d+)`);

    if (match) {
      const [, firstNumber, secondNumber] = match;
      const position = {
        row: parseInt(secondNumber, 10),
        column: parseInt(firstNumber, 10),
      };
      return position;
    }

    return null;
  }

  const handleInputChange = (text: string) => {
    setNewItem(text);
  };

  const saveNewItem = (columnIndex: number, rowIndex: number) => {
    if (newItem) {
      const grade = gradeItems[gradeItems.length - (rowIndex + 1)];
      const path = pathItems[columnIndex];

      if (grade && path) {
        const newPathGradeItem: WithOptional<PathGradeConfigItem, "id"> = {
          name: newItem,
          pathId: path.id,
          gradeId: grade.id,
        };

        const updatedItems = [...pathGradeItems];
        updatedItems[columnIndex][rowIndex] = newPathGradeItem;

        setPathGradeItems(updatedItems);

        setNewItem("");
        setActiveItem(null);
        setIsUpdatingAnItem(false);
      }
    }
  };

  const handleDeleteGradePath = (
    pathPosition: number,
    gradePosition: number,
  ) => {
    const updatedItems = [...pathGradeItems];

    updatedItems[pathPosition][gradePosition] = null;

    setPathGradeItems(updatedItems);
  };

  const startEditing = (
    pathPosition: number,
    gradePosition: number,
    value?: string,
  ) => {
    value
      ? setEditingItem({ pathPosition, gradePosition, value })
      : setActiveItem({ row: gradePosition, column: pathPosition });
    setIsUpdatingAnItem(true);
  };

  const cancelEditing = () => {
    setEditingItem(null);
    setActiveItem(null);
    setIsUpdatingAnItem(false);
    setNewItem("");
  };

  const editItem = () => {
    if (editingItem) {
      const { pathPosition, gradePosition, value } = editingItem;
      const updatedItems = [...pathGradeItems];
      if (updatedItems[pathPosition][gradePosition]) {
        updatedItems[pathPosition][gradePosition] = {
          ...updatedItems[pathPosition][gradePosition],
          name: value,
        } as PathGradeConfigItem;
      }
      setPathGradeItems(updatedItems);
      setEditingItem(null);
      setIsUpdatingAnItem(false);
    }
  };

  const handleEditInputChange = (text: string) => {
    if (editingItem) {
      setEditingItem({ ...editingItem, value: text });
    }
  };

  const isEditingThisItem = (
    rowIndex: number,
    columnIndex: number,
  ): boolean => {
    if (!editingItem) {
      return false;
    }

    return (
      editingItem.pathPosition === rowIndex &&
      editingItem.gradePosition === columnIndex
    );
  };

  const isCreatingNewItem = (): boolean => {
    return !!activeItem;
  };

  return (
    <StyledManagePathsGrades>
      <div className="columns-container background-container">
        {[...Array(pathsCount)].map(() => (
          <div>
            {[...Array(gradesCount)].map(() => (
              <StyledDroppableItem $isInvisible={!isEditable}>
                <BodyTyp
                  variant="1"
                  color={theme.colors.purple}
                  fontWeight={FontWeightEnum.SEMIBOLD}
                >
                  <IconManager
                    size={IconSizeEnum.SMALL}
                    color={theme.colors.grey_3}
                  >
                    <PlusIcon />
                  </IconManager>
                </BodyTyp>
              </StyledDroppableItem>
            ))}
          </div>
        ))}
      </div>
      <div className="columns-container">
        <DragDropContext onDragEnd={handleOnDragEnd}>
          {pathGradeItems.map((pathGradeColumn, ind) => (
            <div className="table-row" key={`row-${ind}`}>
              {pathGradeColumn.map((item, index) =>
                item ? (
                  <Droppable
                    key={`droppable-source-${ind}-${index}`}
                    droppableId={`source-${ind}-${index}`}
                    isDropDisabled={true}
                  >
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        <Draggable
                          draggableId={`draggable-${ind}-${index}`}
                          index={0}
                          isDragDisabled={!isEditable}
                        >
                          {(provided) => (
                            <StyledPathGradeItem
                              key={`item-${ind}-${index}`}
                              $isDragging={false}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              {...provided.draggableProps.style}
                              $isDragDisabled={
                                !isEditable ||
                                editingMainItem ||
                                isCreatingNewItem() ||
                                (isUpdatingAnItem &&
                                  !isEditingThisItem(ind, index))
                              }
                              $isEditable={isEditable}
                              $isNew={isEditingThisItem(ind, index)}
                            >
                              {isEditingThisItem(ind, index) ? (
                                <>
                                  <div className="path-grade-input">
                                    <TextInput
                                      value={editingItem!.value}
                                      maxLength={255}
                                      onChange={handleEditInputChange}
                                      inputSize={InputSizes.SMALL}
                                    />
                                  </div>
                                  <div className="path-grade-actions-editing">
                                    <CheckIconContainer
                                      $isActive={!!editingItem!.value}
                                      onClick={
                                        editingItem!.value ? editItem : () => {}
                                      }
                                    >
                                      <IconManager
                                        size={IconSizeEnum.SMALL}
                                        color={
                                          editingItem!.value
                                            ? theme.colors.white
                                            : theme.colors.grey_4
                                        }
                                      >
                                        <CheckIcon />
                                      </IconManager>
                                    </CheckIconContainer>
                                    <StyledCloseIconContainer>
                                      <IconManager
                                        size={IconSizeEnum.SMALL}
                                        color={theme.colors.purple}
                                        onClick={cancelEditing}
                                      >
                                        <CloseIcon />
                                      </IconManager>
                                    </StyledCloseIconContainer>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div>
                                    <BadgeIndex
                                      content={`${(pathGradeColumn.length - index).toString()}.${(ind + 1).toString()}`}
                                    />
                                    <BodyTyp
                                      variant="1"
                                      color={theme.colors.purple}
                                      fontWeight={FontWeightEnum.SEMIBOLD}
                                    >
                                      {item.name}
                                    </BodyTyp>
                                  </div>
                                  <div className="path-grade-actions">
                                    {isEditable && (
                                      <>
                                        <IconManager
                                          size={IconSizeEnum.SMALL}
                                          onClick={
                                            !isUpdatingAnItem
                                              ? () =>
                                                  startEditing(
                                                    ind,
                                                    index,
                                                    item.name,
                                                  )
                                              : undefined
                                          }
                                          color={
                                            !isUpdatingAnItem
                                              ? theme.colors.purple
                                              : theme.colors.grey_3
                                          }
                                        >
                                          <EditIcon />
                                        </IconManager>
                                        <IconManager
                                          size={IconSizeEnum.SMALL}
                                          onClick={
                                            !isUpdatingAnItem
                                              ? () => setShowModal(true)
                                              : undefined
                                          }
                                          color={
                                            !isUpdatingAnItem
                                              ? theme.colors.purple
                                              : theme.colors.grey_3
                                          }
                                        >
                                          <BinIcon />
                                        </IconManager>
                                      </>
                                    )}
                                    {showModal && (
                                      <ModalDeleteItem
                                        modalLabel={
                                          DELETE_ITEM_MODAL_LABEL +
                                          item.name +
                                          "?"
                                        }
                                        title={DELETE_ITEM_LABEL}
                                        isOpen={showModal}
                                        closeModal={() => setShowModal(false)}
                                        deleteItem={() =>
                                          handleDeleteGradePath(ind, index)
                                        }
                                      />
                                    )}
                                  </div>
                                </>
                              )}
                            </StyledPathGradeItem>
                          )}
                        </Draggable>
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                ) : (
                  <Droppable
                    key={`droppable-destination-${ind}-${index}`}
                    droppableId={`destination-${ind}-${index}`}
                  >
                    {(provided, snapshot) => (
                      <StyledEmptyItem
                        $isDraggingOver={snapshot.isDraggingOver}
                        ref={provided.innerRef}
                        $isEditable={isEditable}
                        {...provided.droppableProps}
                        $createNewItem={
                          activeItem?.row === ind &&
                          activeItem?.column === index &&
                          isUpdatingAnItem
                        }
                        onClick={() =>
                          !isUpdatingAnItem &&
                          isEditable &&
                          startEditing(index, ind)
                        }
                      >
                        {activeItem?.row === ind &&
                        activeItem?.column === index &&
                        isUpdatingAnItem ? (
                          <>
                            <div className="path-grade-input">
                              <TextInput
                                value={newItem}
                                maxLength={255}
                                onChange={handleInputChange}
                                inputSize={InputSizes.SMALL}
                              />
                            </div>
                            <div className="path-grade-actions-editing">
                              <CheckIconContainer
                                $isActive={!!newItem}
                                onClick={() => saveNewItem(ind, index)}
                              >
                                <IconManager
                                  size={IconSizeEnum.SMALL}
                                  color={
                                    newItem
                                      ? theme.colors.white
                                      : theme.colors.grey_4
                                  }
                                >
                                  <CheckIcon />
                                </IconManager>
                              </CheckIconContainer>
                              <StyledCloseIconContainer>
                                <IconManager
                                  size={IconSizeEnum.SMALL}
                                  color={theme.colors.purple}
                                  onClick={cancelEditing}
                                >
                                  <CloseIcon />
                                </IconManager>
                              </StyledCloseIconContainer>
                            </div>
                          </>
                        ) : (
                          <CreateElement $isEditable={isEditable}>
                            <IconManager
                              size={IconSizeEnum.SMALL}
                              color={
                                !isUpdatingAnItem
                                  ? theme.colors.purple
                                  : theme.colors.grey_3
                              }
                            >
                              <AddIcon />
                            </IconManager>
                            <BodyTyp
                              variant="1"
                              color={
                                !isUpdatingAnItem
                                  ? theme.colors.purple
                                  : theme.colors.grey_3
                              }
                              fontWeight={FontWeightEnum.SEMIBOLD}
                            >
                              {CREATE_ELEMENT_LABEL}
                            </BodyTyp>
                          </CreateElement>
                        )}
                      </StyledEmptyItem>
                    )}
                  </Droppable>
                ),
              )}
            </div>
          ))}
        </DragDropContext>
      </div>
    </StyledManagePathsGrades>
  );
};
